import { Alert, AlertTitle, alpha, Box, Button, Divider, Grid, Stack, Typography } from '@mui/material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { AutogiroMandateStatus } from 'src/modules/invoice/components/autogiroMandateStatus';
import { AutogiroMandateModal } from 'src/modules/invoice/components/autogiroMandateModal';
import { AutogiroMandateStatusEnum } from 'src/shared/models/enums/autogiroMandateStatus.enum';
import { DeleteConfirmationDialog } from 'src/shared/components/organisms/deleteConfirmationDialog';
import { deleteAutogiroMandate } from 'src/shared/services/autogiroMandateService';
import { unlinkAutogiroMandateFromDonor } from 'src/shared/services/donorService';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { DonorResponse } from '../models/donor';

interface DonorAutogiroSectionProps {
  donor: DonorResponse;
  refetchCallback?: () => void;
}

export const DonorAutogiroSection = (props: DonorAutogiroSectionProps) => {
  const { donor, refetchCallback } = props;
  const { t } = useTranslation(['member', 'common']);
  const isMounted = useIsMounted();

  const [mandateModalOpen, setMandateModalOpen] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [openUnlinkDialog, setOpenUnlinkDialog] = useState(false);

  const mandate = donor.autogiroMandate;
  // Ett godkänt eller skickat medgivande finns hos Bankgirot och ska makuleras, inte raderas.
  // Se autogiroMandateModal: statusen avgör, inte om filen skickats. Ett avslag kommer alltid
  // efter att medgivandet skickats till Bankgirot, men ligger därmed inte registrerat där.
  const canDeleteMandate =
    mandate.status === AutogiroMandateStatusEnum.New ||
    mandate.status === AutogiroMandateStatusEnum.NotApproved ||
    mandate.status === AutogiroMandateStatusEnum.Error;

  // Backend returnerar alltid ett (tomt) mandatobjekt – saknas riktigt mandat är autogiroMandate_id 0/undefined
  if (!mandate?.autogiroMandate_id) {
    return (
      <Typography variant='body2' color='text.secondary' sx={{ py: 1 }}>
        {t('member:autogiroMandateInfoSection.noMandatePlaceholder')}
      </Typography>
    );
  }

  const mandateResponse = {
    ...mandate,
    bankIdIpAddress: mandate.bankIdIpAddress ?? '',
    bankIdSignedFileName: mandate.bankIdSignedFileName ?? '',
    bankIdUserNonVisibleData: mandate.bankIdUserNonVisibleData ?? '',
    bankIdXMLSignature: mandate.bankIdXMLSignature ?? '',
    autogiroMandateLog: [],
    isSigned: !!mandate.bankIdXMLSignature,
  };

  const handleDeleteMandate = async () => {
    if (!mandate?.autogiroMandate_id) return;
    try {
      await deleteAutogiroMandate(mandate.autogiroMandate_id);
      if (isMounted()) {
        toast.success(<>{t('member:autogiroMandateInfoSection.deleteMandateSuccess')}</>);
        refetchCallback?.();
      }
    } catch (e) {
      if (isMounted()) {
        ConsoleHelper.log(e);
        toast.error(<>{t('member:autogiroMandateInfoSection.deleteMandateError')}</>);
      }
    }
  };

  const handleUnlinkMandate = async () => {
    if (!donor?.id) return;
    try {
      await unlinkAutogiroMandateFromDonor(donor.id);
      if (isMounted()) {
        toast.success(<>{t('member:autogiroMandateInfoSection.unlinkMandateSuccess')}</>);
        refetchCallback?.();
      }
    } catch (e) {
      if (isMounted()) {
        ConsoleHelper.log(e);
        toast.error(<>{t('member:autogiroMandateInfoSection.unlinkMandateError')}</>);
      }
    }
  };

  return (
    <>
      <Grid container>
        <Grid item xs={12} sx={{ backgroundColor: (thm) => alpha(thm.palette.grey[200], 0.35), borderRadius: 1, px: 1.5, py: 1 }}>
          <Typography variant='body2' sx={{ fontWeight: 600 }}>
            {t('member:autogiroMandateInfoSection.mandate')}
          </Typography>

          <Box display='flex' justifyContent='space-between' mt={0.5}>
            <Box display='flex' alignItems='center' gap={1}>
              <Typography variant='body2' mt='1.5px'>
                {mandate.payerNr}
              </Typography>
              <AutogiroMandateStatus item={mandateResponse as any} />
            </Box>

            <Stack direction='row' spacing={2} alignItems='center'>
              <Box>
                <Button
                  variant='text'
                  size='small'
                  sx={{ textTransform: 'none', px: 0, minWidth: 0, marginBottom: -1 }}
                  onClick={() => setMandateModalOpen(true)}
                >
                  {t('common:button.open')}
                </Button>
                <Divider />
              </Box>
              <Box>
                <Button
                  variant='text'
                  size='small'
                  sx={{ textTransform: 'none', px: 0, minWidth: 0, marginBottom: -1 }}
                  onClick={() => setOpenUnlinkDialog(true)}
                >
                  {t('common:button.unlink')}
                </Button>
                <Divider />
              </Box>
              {canDeleteMandate && (
                <Box>
                  <Button
                    variant='text'
                    size='small'
                    sx={{ textTransform: 'none', px: 0, minWidth: 0, marginBottom: -1 }}
                    onClick={() => setOpenDeleteDialog(true)}
                  >
                    {t('common:button.delete')}
                  </Button>
                  <Divider />
                </Box>
              )}
            </Stack>
          </Box>
        </Grid>

        {mandate.status !== undefined && mandate.status !== AutogiroMandateStatusEnum.Approved && (
          <Grid item xs={12} mt={1}>
            <Alert severity='error' icon={false}>
              <AlertTitle sx={{ fontWeight: 600 }}>{t('member:autogiroMandateInfoSection.noMandate')}</AlertTitle>
              {t('member:autogiroMandateInfoSection.noMandateDescription', { payerNr: mandate.payerNr || '' })}
            </Alert>
          </Grid>
        )}
      </Grid>

      {mandateModalOpen && mandate?.autogiroMandate_id && (
        <AutogiroMandateModal
          autogiroMandateId={mandate.autogiroMandate_id}
          onClose={() => setMandateModalOpen(false)}
          onSave={() => setMandateModalOpen(false)}
          modalOpen={mandateModalOpen}
        />
      )}
      <DeleteConfirmationDialog
        title={t('common:confirmDelete')}
        value=''
        modaltext={t('member:autogiroMandateInfoSection.deleteMandate')}
        onClose={(value) => {
          if (value) {
            handleDeleteMandate();
          } else {
            setOpenDeleteDialog(false);
          }
        }}
        open={openDeleteDialog}
      />
      <DeleteConfirmationDialog
        title={t('common:button.unlink')}
        value=''
        modaltext={t('member:autogiroMandateInfoSection.unlinkMandate')}
        onClose={(value) => {
          if (value) {
            handleUnlinkMandate();
          } else {
            setOpenUnlinkDialog(false);
          }
        }}
        open={openUnlinkDialog}
      />
    </>
  );
};
